import { MessageMedium } from 'constants/messageMediums';
import {
  MessageMediumTab,
  OptimizedChangeMetric,
  LiftMetric,
} from 'modules/intelligentInsights/frequencyOptimization/types';

export const INTELLIGENT_INSIGHTS = 'Intelligent Insights';
export const FREQUENCY_OPTIMIZATION_INSIGHTS = 'Frequency Optimization Insights';
export const BREAKDOWN_HEADER = 'Breakdown by channel';
export const FRO_BREAKDOWN_ANCHOR = 'fro-breakdown-anchor-by-message-type';
export const PERFORMANCE_OVER_TIME = 'Performance over time';
export const PERFORMANCE_OVER_TIME_TOOLTIP =
  'Performance of frequency optimized groups over time. Chart data only represents optimizations configured at the weekly cadence.';
export const BREAKDOWN_PERFORMANCE_HEADER = 'performance';
export const EXPORT_CSV = 'Export CSV';
export const LAST_UPDATED_ON = 'Last updated on';
export const VIEWING = 'Viewing';

export const OPEN_RATE = 'open rate';
export const CLICK_RATE = 'click rate';
export const UNSUBSCRIBE_RATE = 'unsubscribe rate';
export const RATE = 'rate';
export const MESSAGES_PER_WEEK = 'messages per week';
export const INCREASE = 'increase';
export const DECREASE = 'decrease';
export const IN = 'in';
export const AVERAGE_SENDS_ACROSS_CHANNELS_TOOLTIP =
  'The average number of sends per user in the optimized group for all channels and message types with an active optimization this period';

export const PERFORMANCE_OVER_TIME_EXPORT_HEADERS = [
  {
    label: 'Date',
    key: 'endDate',
  },
  {
    label: 'Channel',
    key: 'messageMedium',
  },
  {
    label: 'Open Rate',
    key: 'optimizedOpenRate',
  },
  {
    label: 'Click Rate',
    key: 'optimizedClickRate',
  },
  {
    label: 'Unsubscribe Rate',
    key: 'optimizedUnsubscribeRate',
  },
  {
    label: 'Frequency Period',
    key: 'frequencyPeriod',
  },
];
export const PERFORMANCE_OVER_TIME_EXPORT_SUCCESS = 'Success! Performance over time data exported.';

export const OPTIMIZED_METRIC_TEXT: Record<OptimizedChangeMetric, string> = {
  [OptimizedChangeMetric.OptimizedClickRateChange]: CLICK_RATE,
  [OptimizedChangeMetric.OptimizedOpenRateChange]: OPEN_RATE,
  [OptimizedChangeMetric.OptimizedUnsubscribeRateChange]: UNSUBSCRIBE_RATE,
};
export const OPTIMIZED_METRIC_NO_CHANGE = 'No change this period';

export const OPTIMIZED_LIFT_TEXT: Record<LiftMetric, string> = {
  [LiftMetric.ClickRateLift]: CLICK_RATE,
  [LiftMetric.OpenRateLift]: OPEN_RATE,
  [LiftMetric.UnsubscribeRateLift]: UNSUBSCRIBE_RATE,
};
export const OPTIMIZED_LIFT_NO_CHANGE = 'No lift this period';

export const AVERAGE_SENDS_PER_MEDIUM = 'Average sends across channels';
export const BIGGEST_LIFT = 'Biggest lift';
export const BIGGEST_CHANGE = 'Biggest change';
export const MORE_ABOUT_THIS_CHANGE = 'More about this change';

export const BREAKDOWN_AUDIENCE_LIFT = 'Audience lift analysis';

export const BREAKDOWN_AUDIENCE_MESSAGE_TYPES_MAX = 3;

export const BREAKDOWN_AUDIENCE_MESSAGE_TYPES_UNCONFIGURED_TITLE = 'No optimizations configured';
export const BREAKDOWN_AUDIENCE_MESSAGE_TYPES_UNCONFIGURED_SUBTITLE =
  "Looks like there weren't any optimizations configured for this channel during your selected time period";
export const BREAKDOWN_AUDIENCE_MESSAGE_TYPES_ERROR_TITLE = 'Oops!';
export const BREAKDOWN_AUDIENCE_MESSAGE_TYPES_ERROR_SUBTITLE =
  'We had a problem fetching this data.';
export const BREAKDOWN_AUDIENCE_MESSAGE_TYPES_ERROR_SUBTITLE_LINK = 'Try refreshing the page.';
export const BREAKDOWN_AUDIENCE_MESSAGE_TYPE_TOP_CAMPAIGNS = 'Top campaigns';
export const BREAKDOWN_AUDIENCE_MESSAGE_TYPE_COLLAPSE_CAMPAIGNS = 'Top contributing campaigns';
export const BREAKDOWN_AUDIENCE_MESSAGE_TYPE_CAMPAIGN_DEFAULT_METRIC_TEXT = 'N/A';
export const BREAKDOWN_AUDIENCE_MESSAGE_TYPE_CAMPAIGNS_TOGGLETIP =
  'Top campaigns this period contributing to engagement lift for this message type';
export const BREAKDOWN_MESSAGE_TYPE_EXPORT_HEADERS = [
  {
    label: 'Message Type',
    key: 'name',
  },
  {
    label: 'Open Rate Lift',
    key: 'openRateLift',
  },
  {
    label: 'Click Rate Lift',
    key: 'clickRateLift',
  },
  {
    label: 'Unsubscribe Rate Lift',
    key: 'unsubscribeRateLift',
  },
  {
    label: 'Top Contributing Campaign ID',
    key: 'topCampaigns',
  },
];
export const BREAKDOWN_MESSAGE_TYPE_UNSUBSCRIBE_TOOLTIP =
  'A negative value means that your optimized group has a lower unsubscribe rate than the holdout group';
export const BREAKDOWN_MESSAGE_TYPE_EXPORT_SUCCESS = 'Success! Lift by message type data exported.';

export const BREAKDOWN_VIEW_IN_SEGMENTATION = 'View list in segmentation';

export const BREAKDOWN_AUDIENCE_AVERAGE_SENDS = 'Average sends';

export const BREAKDOWN_AUDIENCE_AVERAGE_SENDS_PER_WEEK = 'messages per week for optimized users';

export const BREAKDOWN_AUDIENCE_MESSAGE_TYPES_HEADER = 'Lift by message type';

export const BREAKDOWN_AUDIENCE_AVERAGE_SENDS_WITH_HOLDOUT = {
  PRENUMBER: 'On average, the optimized group received',
  POSTNUMBER: 'compared to the holdout.',
};

export const AVERAGE_SENDS_DIFFS = {
  FEWER: 'fewer messages',
  EQUAL: 'a similar number of sends',
  MORE: 'more messages',
};

export const BREAKDOWN_AUDIENCE_ENGAGEMENT_HEADER = 'Engagement lift';

export const BREAKDOWN_AUDIENCE_ENGAGEMENT_METRICS = {
  OPEN_RATE: 'Open rate',
  CLICK_RATE: 'Click rate',
  UNSUBSCRIBE_RATE: 'Unsubscribe rate',
};

export const BREAKDOWN_AUDIENCE_SIZE_HEADER = 'Audience size';
export const BREAKDOWN_AUDIENCE_SIZE_OPTIMIZED_USERS = 'optimized users';
export const BREAKDOWN_AUDIENCE_SIZE_OPTIMIZED_USERS_TOOLTIP =
  'Number of users from the overall treatment group who were assigned a frequency optimized send cap.';

export const createBreakdownAudienceSummary = (optimizedTotal: string, percentValue: string) =>
  `The ${optimizedTotal} optimized users made up ${percentValue} of the total treatment group for this period.`;

export const BREAKDOWN_TABS: Array<MessageMediumTab> = [
  MessageMedium.Email,
  MessageMedium.Push,
  MessageMedium.SMS,
];

export const FRO_PREVIEW_KEY = 'fro-preview';

export enum IntelligentInsightsTab {
  BA = 'brandAffinityInsights',
  STO = 'sendTimeOptimizationInsights',
  FRO = 'froReporting',
}

export const INTELLIGENT_INSIGHTS_DISPLAY_NAME = {
  [IntelligentInsightsTab.BA]: 'Brand Affinity',
  [IntelligentInsightsTab.STO]: 'Send Time Optimization',
  [IntelligentInsightsTab.FRO]: 'Frequency Optimization',
};

export const intelligentInsightsTabOrder = [
  IntelligentInsightsTab.BA,
  IntelligentInsightsTab.STO,
  IntelligentInsightsTab.FRO,
];
